'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { useRole } from '@/lib/hooks/useRole';
import { 
  Home, 
  Sparkles, 
  Puzzle, 
  FileText, 
  Bell, 
  Book, 
  HelpCircle,
  Settings,
  MessageSquare,
  Calendar,
  Activity,
  Database,
  Users,
  History,
  ClipboardList
} from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  iconName: string;
  href: string;
  active?: boolean;
}

// Lucide React icon components with theme-aware styling
const icons = {
  home: (className: string) => <Home data-component-id="src/components/common/SidebarMenu.tsx:35:31" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="35" data-component-file="SidebarMenu.tsx" data-component-name="Home" data-component-content="%7B%22elementName%22%3A%22Home%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  sparkles: (className: string) => <Sparkles data-component-id="src/components/common/SidebarMenu.tsx:36:35" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="36" data-component-file="SidebarMenu.tsx" data-component-name="Sparkles" data-component-content="%7B%22elementName%22%3A%22Sparkles%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  puzzle: (className: string) => <Puzzle data-component-id="src/components/common/SidebarMenu.tsx:37:33" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="37" data-component-file="SidebarMenu.tsx" data-component-name="Puzzle" data-component-content="%7B%22elementName%22%3A%22Puzzle%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  pr: (className: string) => <FileText data-component-id="src/components/common/SidebarMenu.tsx:38:29" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="38" data-component-file="SidebarMenu.tsx" data-component-name="FileText" data-component-content="%7B%22elementName%22%3A%22FileText%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  bell: (className: string) => <Bell data-component-id="src/components/common/SidebarMenu.tsx:39:31" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="39" data-component-file="SidebarMenu.tsx" data-component-name="Bell" data-component-content="%7B%22elementName%22%3A%22Bell%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  book: (className: string) => <Book data-component-id="src/components/common/SidebarMenu.tsx:40:31" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="40" data-component-file="SidebarMenu.tsx" data-component-name="Book" data-component-content="%7B%22elementName%22%3A%22Book%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  help: (className: string) => <HelpCircle data-component-id="src/components/common/SidebarMenu.tsx:41:31" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="41" data-component-file="SidebarMenu.tsx" data-component-name="HelpCircle" data-component-content="%7B%22elementName%22%3A%22HelpCircle%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  settings: (className: string) => <Settings data-component-id="src/components/common/SidebarMenu.tsx:42:35" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="42" data-component-file="SidebarMenu.tsx" data-component-name="Settings" data-component-content="%7B%22elementName%22%3A%22Settings%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  message: (className: string) => <MessageSquare data-component-id="src/components/common/SidebarMenu.tsx:43:34" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="43" data-component-file="SidebarMenu.tsx" data-component-name="MessageSquare" data-component-content="%7B%22elementName%22%3A%22MessageSquare%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  calendar: (className: string) => <Calendar data-component-id="src/components/common/SidebarMenu.tsx:44:35" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="44" data-component-file="SidebarMenu.tsx" data-component-name="Calendar" data-component-content="%7B%22elementName%22%3A%22Calendar%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  activity: (className: string) => <Activity data-component-id="src/components/common/SidebarMenu.tsx:45:35" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="45" data-component-file="SidebarMenu.tsx" data-component-name="Activity" data-component-content="%7B%22elementName%22%3A%22Activity%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  database: (className: string) => <Database data-component-id="src/components/common/SidebarMenu.tsx:46:35" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="46" data-component-file="SidebarMenu.tsx" data-component-name="Database" data-component-content="%7B%22elementName%22%3A%22Database%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  users: (className: string) => <Users data-component-id="src/components/common/SidebarMenu.tsx:47:32" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="47" data-component-file="SidebarMenu.tsx" data-component-name="Users" data-component-content="%7B%22elementName%22%3A%22Users%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  history: (className: string) => <History data-component-id="src/components/common/SidebarMenu.tsx:48:34" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="48" data-component-file="SidebarMenu.tsx" data-component-name="History" data-component-content="%7B%22elementName%22%3A%22History%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />,
  clipboard: (className: string) => <ClipboardList data-component-id="src/components/common/SidebarMenu.tsx:49:36" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="49" data-component-file="SidebarMenu.tsx" data-component-name="ClipboardList" data-component-content="%7B%22elementName%22%3A%22ClipboardList%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} size={16} />
};

const SidebarMenu = () => {
  const pathname = usePathname()
  const { isAdmin } = useRole()
  const baseMenuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      iconName: 'home',
      href: '/dashboard',
      active: pathname === '/dashboard'
    },
    {
      id: 'regintelx',
      label: 'DigitalReg GPT',
      iconName: 'sparkles',
      href: '/regintelx',
      active: pathname === '/regintelx'
    },
    {
      id: 'regmonitor',
      label: 'DigitalReg Monitor',
      iconName: 'pr',
      href: '/regmonitor',
      active: pathname === '/regmonitor'
    },
    // {
    //   id: 'scorecard',
    //   label: 'DigitalReg Scorecard',
    //   iconName: 'puzzle',
    //   href: '/scorecard',
    //   active: pathname === '/scorecard'
    // },
    {
      id: 'drsr',
      label: 'Digital Regulatory &nbsp;&nbsp; Supervision Record',
      iconName: 'clipboard',
      href: '/drsr',
      active: pathname?.startsWith('/drsr')
    },
    // {
    //   id: 'schedule-update',
    //   label: 'Document Updates',
    //   iconName: 'calendar',
    //   href: '/schedule-update',
    //   active: pathname === '/schedule-update'
    // },
    // {
    //   id: 'scrapped-dates',
    //   label: 'Regulatory Activity',
    //   iconName: 'activity',
    //   href: '/scrapped-dates',
    //   active: pathname === '/scrapped-dates'
    // },
    // {
    //   id: 'alerts',
    //   label: 'Alerts & Preferences',
    //   iconName: 'bell',
    //   href: '/alerts',
    //   active: pathname === '/alerts'
    // },
    // {
    //   id: 'resources',
    //   label: 'Resources',
    //   iconName: 'book',
    //   href: '/resources',
    //   active: pathname === '/resources'
    // },
    {
      id: 'notifications',
      label: 'Notifications',
      iconName: 'message',
      href: '/notifications',
      active: pathname === '/notifications'
    },
    {
      id: 'contact',
      label: 'Contact Us',
      iconName: 'help',
      href: '/contact',
      active: pathname === '/contact'
    }
  ]

  const adminMenuItems: MenuItem[] = [
    {
      id: 'data-management',
      label: 'Data Management',
      iconName: 'database',
      href: '/data-management',
      active: pathname === '/data-management'
    },
    {
      id: 'user-management',
      label: 'User Management',
      iconName: 'users',
      href: '/user-management',
      active: pathname === '/user-management'
    },
    // {
    //   id: 'scorecard-history',
    //   label: 'Scorecard History',
    //   iconName: 'history',
    //   href: '/scorecard-history',
    //   active: pathname === '/scorecard-history'
    // }
  ]

  // Combine base menu items with admin items if user is admin
  const menuItems = isAdmin() 
    ? [...baseMenuItems, ...adminMenuItems]
    : baseMenuItems

  return (
    <nav data-component-id="src/components/common/SidebarMenu.tsx:165:4" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="165" data-component-file="SidebarMenu.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20flex-col%20justify-start%20items-center%20w-full%22%7D" className="flex flex-col justify-start items-center w-full">
      <div data-component-id="src/components/common/SidebarMenu.tsx:166:6" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="166" data-component-file="SidebarMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20justify-start%20items-center%20w-full%20pl-%5B15px%5D%22%7D" className="flex flex-col justify-start items-center w-full pl-[15px]">
        {menuItems.map((item) => {
          const IconComponent = icons[item.iconName as keyof typeof icons];
          const iconClassName = twMerge(
            'w-4 h-4 ml-3 transition-colors duration-200',
            item.active
              ? 'stroke-[#295fd8] dark:stroke-[#60a5fa]'
              : 'stroke-[#191E2B] dark:stroke-[#e5e7eb]'
          );

          return (
            <Link data-component-id="src/components/common/SidebarMenu.tsx:177:12" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="177" data-component-file="SidebarMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
              key={item.id}
              href={item.href}
              className={twMerge(
                'flex flex-row justify-start items-center w-full p-[10px] pl-[0px] transition-colors duration-200',
                item.active
                  ? 'bg-fifth rounded-bl-[100px] rounded-tl-[100px]'
                  : 'hover:bg-fifth bg-secondary rounded-bl-[100px] rounded-tl-[100px]'
              )}
            >
              {IconComponent && IconComponent(iconClassName)}
              <span data-component-id="src/components/common/SidebarMenu.tsx:188:14" data-component-path="src/components/common/SidebarMenu.tsx" data-component-line="188" data-component-file="SidebarMenu.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B13px%5D%20font-medium%20leading-sm%20text-left%20text-white%20ml-2%20cursor-pointer%22%7D" className="text-[13px] font-medium leading-sm text-left text-white ml-2 cursor-pointer" dangerouslySetInnerHTML={{ __html: item.label }}></span>
            </Link>
          );
        })}
      </div>
    </nav>
  )
}

export default SidebarMenu